/**
 * Adapter around the native HaxBall disc solver bundled in game2.js.
 *
 * This module intentionally owns no DOM, replay cursor, or analytics state. It
 * prepares a native stadium (`h.Lk`), advances `Fa.C(1)` one tick at a time,
 * and exposes plain snapshots for Gym/AI consumers.
 */
import {
  NativeStadium,
  NativePhysicsWorld,
  NativeDisc
} from '../game2';

const TICK_RATE = 60;
const ALL_MASK = 63;
const MASKS = { all: ALL_MASK, ball: 1, red: 2, blue: 4, redko: 8, blueko: 16, wall: 32, kick: 64, score: 128 };

function number(value, fallback = 0) {
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}
function vector(value, fallback = { x: 0, y: 0 }) {
  if (!value || typeof value !== 'object') return { ...fallback };
  return { x: number(value.x ?? value[0]), y: number(value.y ?? value[1]) };
}
function mask(value, fallback = ALL_MASK) {
  if (value === undefined || value === null) return fallback;
  if (Array.isArray(value)) return value.reduce((sum, item) => sum | mask(item, 0), 0);
  if (typeof value === 'string') return value.split(/[ ,|]/).filter(Boolean).reduce((sum, item) => sum | (MASKS[item.toLowerCase()] || 0), 0);
  return Number(value) | 0;
}
function clone(value) {
  return JSON.parse(JSON.stringify(value));
}
function copyDisc(disc, raw = {}) {
  const position = vector(raw.position || raw.pos || raw.a);
  const velocity = vector(raw.velocity || raw.vel || raw.D);
  const gravity = vector(raw.gravity || raw.oa);
  disc.a.x = position.x;
  disc.a.y = position.y;
  disc.D.x = velocity.x;
  disc.D.y = velocity.y;
  disc.oa.x = gravity.x;
  disc.oa.y = gravity.y;
  if (raw.radius !== undefined || raw.Z !== undefined) disc.Z = number(raw.radius ?? raw.Z, disc.Z);
  if (raw.bCoef !== undefined || raw.m !== undefined) disc.m = number(raw.bCoef ?? raw.m, disc.m);
  if (raw.invMass !== undefined || raw.aa !== undefined) disc.aa = number(raw.invMass ?? raw.aa, disc.aa);
  if (raw.damping !== undefined || raw.Ca !== undefined) disc.Ca = number(raw.damping ?? raw.Ca, disc.Ca);
  if (raw.cMask !== undefined || raw.h !== undefined) disc.h = mask(raw.cMask ?? raw.h, disc.h);
  if (raw.cGroup !== undefined || raw.v !== undefined) disc.v = mask(raw.cGroup ?? raw.v, disc.v);
  return disc;
}
function plainDisc(disc) {
  return {
    position: { x: disc.a.x, y: disc.a.y },
    velocity: { x: disc.D.x, y: disc.D.y },
    gravity: { x: disc.oa.x, y: disc.oa.y },
    radius: disc.Z,
    bCoef: disc.m,
    invMass: disc.aa,
    damping: disc.Ca,
    cMask: disc.h,
    cGroup: disc.v
  };
}
function teamGroup(team) {
  const value = String(team || '').toLowerCase();
  return value === 'red' || value === '1' ? MASKS.red : value === 'blue' || value === '2' ? MASKS.blue : 0;
}
function inputVector(input) {
  const value = vector(input);
  const x = value.x === 0 ? 0 : value.x > 0 ? 1 : -1;
  const y = value.y === 0 ? 0 : value.y > 0 ? 1 : -1;
  const diagonal = x && y ? Math.SQRT1_2 : 1;
  return { x: x * diagonal, y: y * diagonal };
}
function nativeInput(input) {
  const value = inputVector(input);
  return { x: value.x, y: value.y };
}

export function createGymNativeEngine(map, initial = {}) {
  const mapJson = typeof map === 'string' ? map : JSON.stringify(map || {});
  const stadium = new NativeStadium();
  stadium.Lk(mapJson);
  const world = new NativePhysicsWorld();
  world.J = stadium.J;
  world.qa = stadium.qa;
  world.U = stadium.U;
  world.pb = stadium.pb;
  const staticDiscs = stadium.F.map(source => {
    const disc = typeof source.rp === 'function' ? source.rp() : source;
    const copy = new NativeDisc();
    if (typeof NativeDisc.qd === 'function') NativeDisc.qd(copy, disc);
    else copyDisc(copy, plainDisc(disc));
    return copy;
  });
  const initialPlayers = Array.isArray(initial.players) ? initial.players : [];
  const playerMeta = new Map();
  const playersById = new Map();
  let state = null;
  let ball = null;

  const makePlayerDisc = raw => {
    const physics = stadium.ge || {};
    const disc = new NativeDisc();
    disc.Z = number(raw.radius, number(physics.Z, 15));
    disc.m = number(raw.bCoef, number(physics.m, .5));
    disc.aa = number(raw.invMass, number(physics.aa, .5));
    disc.Ca = number(raw.damping, number(physics.Ca, .96));
    disc.h = mask(raw.cMask, 39);
    disc.v = mask(raw.cGroup, teamGroup(raw.team) | number(physics.v, 0));
    copyDisc(disc, raw);
    return disc;
  };

  function rebuild(next = {}) {
    world.F = staticDiscs.map(source => {
      const copy = new NativeDisc();
      if (typeof NativeDisc.qd === 'function') NativeDisc.qd(copy, source);
      else copyDisc(copy, plainDisc(source));
      return copy;
    });
    if (!world.F.length) {
      const fallback = new NativeDisc();
      fallback.Z = 10; fallback.m = .5; fallback.aa = 1; fallback.Ca = .99; fallback.h = 63; fallback.v = 193;
      world.F.push(fallback);
    }
    ball = world.F[0];
    copyDisc(ball, { ...(next.ball || {}), cMask: next.ball?.cMask ?? 63, cGroup: next.ball?.cGroup ?? 193, radius: next.ball?.radius ?? 10, invMass: next.ball?.invMass ?? 1, bCoef: next.ball?.bCoef ?? .5, damping: next.ball?.damping ?? .99 });
    playerMeta.clear();
    playersById.clear();
    const players = Array.isArray(next.players) ? next.players : initialPlayers;
    players.forEach((raw, index) => {
      const id = raw.id ?? index;
      const disc = makePlayerDisc(raw);
      world.F.push(disc);
      playersById.set(String(id), disc);
      playerMeta.set(String(id), { ...raw, id, input: nativeInput(raw.input), kickCooldown: Math.max(0, number(raw.kickCooldown, 0)), active: raw.active !== false });
    });
    state = { ...clone(next), tick: number(next.tick, 0), stadium: next.stadium || map, ball: plainDisc(ball), players: players.map((raw, index) => ({ ...clone(raw), id: raw.id ?? index })) };
    syncState();
    return state;
  }

  function syncState() {
    state = {
      ...state,
      tick: number(state?.tick, 0),
      ball: { ...(state?.ball || {}), ...plainDisc(ball) },
      players: state.players.map(raw => {
        const disc = playersById.get(String(raw.id));
        const meta = playerMeta.get(String(raw.id));
        return disc ? { ...raw, ...plainDisc(disc), input: meta?.input || nativeInput(raw.input), kickCooldown: meta?.kickCooldown || 0 } : raw;
      })
    };
    return state;
  }

  function goalEvent(previousBall) {
    const goals = Array.isArray(map?.goals) ? map.goals : [];
    const current = state.ball.position;
    for (const goal of goals) {
      const points = [goal.p0, goal.p1].map(vector);
      if (points.length < 2) continue;
      const x = points[0].x;
      const minY = Math.min(points[0].y, points[1].y) - state.ball.radius;
      const maxY = Math.max(points[0].y, points[1].y) + state.ball.radius;
      if (current.y < minY || current.y > maxY) continue;
      if ((previousBall.x - x) * (current.x - x) <= 0 && Math.abs(current.x - x) < 20) return { type: 'goal', team: goal.team, x, y: current.y };
    }
    return null;
  }

  function step(controls = {}) {
    const previousBall = { ...state.ball.position };
    state.players.forEach(raw => {
      const meta = playerMeta.get(String(raw.id));
      const disc = playersById.get(String(raw.id));
      if (!meta || !disc || meta.active === false) return;
      const control = controls[raw.id] || controls[String(raw.id)] || {};
      const input = nativeInput(control.input || control);
      meta.input = input;
      meta.kickCooldown = Math.max(0, number(meta.kickCooldown, 0) - 1);
      const kick = Boolean(control.kick);
      const physics = stadium.ge || {};
      const acceleration = kick ? number(physics.Te, .07) : number(physics.Ce, .1);
      disc.D.x += input.x * acceleration;
      disc.D.y += input.y * acceleration;
      disc.Ca = kick ? number(physics.Ue, .96) : number(physics.Ca, .96);
      if (kick && meta.kickCooldown <= 0) {
        const dx = ball.a.x - disc.a.x;
        const dy = ball.a.y - disc.a.y;
        const distance = Math.hypot(dx, dy) || 1;
        if (distance - disc.Z - ball.Z < 4) {
          const nx = dx / distance;
          const ny = dy / distance;
          ball.D.x += nx * number(physics.Re, 5) * ball.aa;
          ball.D.y += ny * number(physics.Re, 5) * ball.aa;
          const kickback = number(physics.Se, 0);
          disc.D.x -= nx * kickback * disc.aa;
          disc.D.y -= ny * kickback * disc.aa;
          meta.kickCooldown = 2;
        }
      }
    });
    world.C(1);
    state.tick += 1;
    syncState();
    const events = [];
    const goal = goalEvent(previousBall);
    if (goal) events.push(goal);
    return { state: clone(state), events };
  }

  function snapshot() { return clone(state); }
  function reset(next = initial) { return rebuild(next); }
  function predict(milliseconds = 0) {
    const saved = snapshot();
    const ticks = Math.max(0, Math.min(12, Math.round(number(milliseconds, 0) * TICK_RATE / 1000)));
    let predicted = saved;
    try {
      for (let index = 0; index < ticks; index += 1) {
        const controls = {};
        predicted.players.forEach(player => { controls[player.id] = { input: player.input, kick: false }; });
        predicted = step(controls).state;
      }
      return predicted;
    } finally {
      // Prediction is render-only; restore even if a malformed map/disc throws.
      rebuild(saved);
    }
  }

  rebuild(initial);
  return { step, predict, reset, snapshot, getState: () => clone(state), stadium, world };
}
